import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import {
  NewReleases as NewReleasesIcon,
  AutoFixHigh as ImproveIcon,
  BugReport as FixIcon,
  AddCircleOutline as NewIcon,
  Close as CloseIcon,
  RocketLaunch as RocketIcon,
} from '@mui/icons-material';
import { CHANGELOG, APP_VERSION, ChangeType } from '../config/changelog';

interface ChangelogModalProps {
  open: boolean;
  onClose: () => void;
}

const TYPE_CONFIG: Record<ChangeType, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  new:     { label: 'Novo',    color: '#065f46', bg: '#d1fae5', icon: <NewIcon     sx={{ fontSize: 14 }} /> },
  improve: { label: 'Melhoria', color: '#1e40af', bg: '#dbeafe', icon: <ImproveIcon sx={{ fontSize: 14 }} /> },
  fix:     { label: 'Correção', color: '#92400e', bg: '#fef3c7', icon: <FixIcon     sx={{ fontSize: 14 }} /> },
};

const ChangelogModal: React.FC<ChangelogModalProps> = ({ open, onClose }) => {
  const latest = CHANGELOG[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header com gradiente */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          px: 3,
          pt: 3,
          pb: 2.5,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'rgba(255,255,255,0.6)',
            '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RocketIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', lineHeight: 1 }}>
              VettorPrint CRM
            </Typography>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              Novas Atualizações
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <Chip
            icon={<NewReleasesIcon sx={{ fontSize: '14px !important', color: '#f59e0b !important' }} />}
            label={`v${APP_VERSION}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(245, 158, 11, 0.2)',
              color: '#fbbf24',
              fontWeight: 700,
              border: '1px solid rgba(245, 158, 11, 0.3)',
              fontSize: '12px',
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
            {latest?.date}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        {CHANGELOG.map((entry, entryIdx) => (
          <Box key={entry.version}>
            {entryIdx > 0 && (
              <Box sx={{ px: 3, py: 1.5, backgroundColor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`v${entry.version}`}
                    size="small"
                    sx={{ fontSize: '11px', fontWeight: 600, backgroundColor: '#e2e8f0', color: '#64748b' }}
                  />
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    {entry.date} · {entry.title}
                  </Typography>
                </Box>
              </Box>
            )}

            {entryIdx === 0 && (
              <Box sx={{ px: 3, pt: 2.5, pb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  {entry.title}
                </Typography>
              </Box>
            )}

            <Box sx={{ px: 3, pb: entryIdx === 0 ? 1 : 2 }}>
              {entry.items.map((item, idx) => {
                const cfg = TYPE_CONFIG[item.type];
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 1,
                      borderBottom: idx < entry.items.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.4,
                        px: 0.8,
                        py: 0.3,
                        borderRadius: 1,
                        backgroundColor: cfg.bg,
                        color: cfg.color,
                        flexShrink: 0,
                        mt: 0.1,
                        minWidth: 72,
                        justifyContent: 'center',
                      }}
                    >
                      {cfg.icon}
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: cfg.color }}>
                        {cfg.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.5, fontSize: '13px' }}>
                      {item.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {entryIdx < CHANGELOG.length - 1 && <Divider />}
          </Box>
        ))}
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          Você sempre pode consultar as atualizações aqui.
        </Typography>
        <Button
          variant="contained"
          onClick={onClose}
          size="small"
          sx={{
            background: 'linear-gradient(135deg, #1e293b, #334155)',
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #0f172a, #1e293b)' },
          }}
        >
          Entendi!
        </Button>
      </Box>
    </Dialog>
  );
};

export default ChangelogModal;
